import * as React from "react";
import { isEqual } from "lodash";
import Flicking, { Plugin, ViewportSlot } from "@egjs/react-flicking";
import { AutoPlay, Pagination, Arrow } from "@egjs/flicking-plugins";
import { TUseXElement, useXElement } from "../../../hooks/useXElement";
import {
  IXCarouselProps,
  TXCarouselProps,
} from "../../../utils/interfaces/containers/XCarousel.interfaces";
import { Skeleton, Stack } from "@mui/material";
import { generateBorder } from "../../../lib/generateBorder";
import { generateRadius } from "../../../lib/generateRadius";
import XArticleProvider from "../../../context/XArticleProvider";
import XArticle from "../../common/XArticle";
import { TCoreXElement } from "../../../utils/types/core/CoreXelement";
import { useArticles } from "../../../hooks/useArticles";

const compareProps = (prevProps: any, nextProps: any) => {
  return isEqual(prevProps, nextProps);
};

const XCarouselCore: TCoreXElement<IXCarouselProps> = React.memo(
  ({ index, props, DynamicXElement }) => {
    // WE TRACK THE BREAK POINTS CHANGES

    // WE ARE GETTING THE CURRENT ELEMENT FROM THE ATOM FAMILY
    const { xElement } = useXElement<TUseXElement<TXCarouselProps>>(index);

    const { articles } = useArticles({ limit: 8, ...xElement.dataFetching });

    const {
      height = "300px",
      columns = 1,
      gap = "20px",
      autoPlay = false,
      // TODO IMPLEMENT DIRECTION
      direction = "horizontal",
      loop = false,
      align = "prev",

      // DOTS
      dotMode = "bullet",
      dotColor,
      dotHeight,
      dotWidth,
      dotBorder,
      dotRadius,
      dotActivedColor,
      dotActivedHeight,
      dotActivedWidth,
      dotActivedBorder,

      // ARROW
      arrowPosition = "in",
      arrowVisibility = "always",
      arrowWeight = "bold",
      arrowBgColor,
      arrowWidth,
      arrowBorder,
      arrowRadius,
      arrowColor,
      arrowFontSize,
      arrowHeight,
      arrowHoverBgColor,
      arrowHoverBorder,
      arrowHoverRadius,
      arrowHoverColor,
      arrowHoverFontSize = "20px",
      arrowHoverHeight,
      arrowHoverWidth,
    } = props;

    const plugins: Plugin[] = [];

    if (autoPlay) {
      plugins.push(
        new AutoPlay({ duration: 5000, direction: "NEXT", stopOnHover: true })
      );
    }

    if (dotMode !== "none") {
      plugins.push(new Pagination({ type: dotMode }));
    }

    if (arrowVisibility !== "none") {
      plugins.push(new Arrow({ parentEl: document.body }));
    }

    let elements = DynamicXElement
      ? xElement.elementsIndexes.map((id, p) => (
          <Stack
            key={id}
            height="100%"
            minHeight={height ? height : "auto"}
            width={`calc(100%-${columns !== 1 ? gap : "0px"})`}
            mr={columns !== 1 ? gap : "0px"}
          >
            {
              <DynamicXElement
                key={id}
                index={id}
                hierachy={xElement.hierachy}
                child={null}
                position={p}
              />
            }
          </Stack>
        ))
      : [];

    if (articles && xElement.dataFetching && xElement.dataFetching.enabled) {
      elements = articles.map((article, i) => {
        const id = article.id ?? i.toString();

        return (
          <Stack
            key={id}
            height="100%"
            minHeight={height ? height : "auto"}
            width={`calc(100%-${columns !== 1 ? gap : "0px"})`}
            alignItems="center"
            justifyContent="center"
            mr={columns !== 1 ? gap : "0px"}
          >
            <XArticleProvider data={article}>
              <XArticle index={id} />
            </XArticleProvider>
          </Stack>
        );
      });
    }

    const arrowCircle = arrowBgColor ? " is-circle" : "";

    const arrowOutside = arrowPosition === "out" ? " is-outside" : "";

    const arrowThin = arrowWeight === "thin" ? " is-thin" : "";

    return (
      <Stack
        sx={{
          height: "min-content",
          width: "100%",
          position: "relative",

          "& .panel": {
            width: "100%",
          },

          "& .flicking-camera": {
            // gap: columns !== 1 ? gap : 0,
          },

          "& .flicking-pagination": {
            // DOT
            "& .flicking-pagination-bullet": {
              transition: "linear all 0.2s",
              bgcolor: dotColor,
              height: dotHeight,
              width: dotWidth,
              border: generateBorder(dotBorder),
              borderRadius: generateRadius(dotRadius, "4px"),

              "&.flicking-pagination-bullet-active": {
                bgcolor: dotActivedColor,
                height: dotActivedHeight,
                width: dotActivedWidth,
                border: generateBorder(dotActivedBorder),
              },
            },
          },

          // ARROW
          '& [class*="flicking-arrow-"]': {
            borderRadius: generateRadius(arrowRadius, "50%"),
            width: arrowWidth,
            height: arrowHeight,

            "&:not(.flicking-arrow-disabled)": {
              bgcolor: arrowBgColor,
              border: generateBorder(arrowBorder),

              "&:before, &:after": {
                bgcolor: arrowColor,
                width: arrowFontSize,
              },
            },

            "&:hover": {
              borderRadius: generateRadius(arrowHoverRadius, "50%"),
              width: arrowHoverWidth,
              height: arrowHoverHeight,

              "&:not(.flicking-arrow-disabled)": {
                bgcolor: arrowHoverBgColor,
                border: generateBorder(arrowHoverBorder),

                "&:before, &:after": {
                  bgcolor: arrowHoverColor,
                  width: arrowHoverFontSize,
                },
              },
            },
          },
        }}
      >
        <Flicking
          plugins={plugins}
          align={align}
          panelsPerView={columns}
          circular={loop}
          horizontal={direction === "horizontal"}
        >
          {elements}
          {elements.length === 0 && [
            ...Array(10)
              .fill("col")
              .map((_, index) => (
                <Skeleton width="100%" height={300} key={index.toString()} />
              )),
          ]}
          <ViewportSlot>
            <div className="flicking-pagination" />
            <span
              className={`flicking-arrow-prev${
                arrowCircle + arrowOutside + arrowThin
              }`}
            />
            <span
              className={`flicking-arrow-next${
                arrowCircle + arrowOutside + arrowThin
              }`}
            />
          </ViewportSlot>
        </Flicking>
      </Stack>
    );
  },
  compareProps
);

export default XCarouselCore;
